import React, { useEffect, useState } from "react";

// eslint-disable-next-line import/no-extraneous-dependencies
import {
    RiAccountCircleLine,
    RiBookMarkedLine,
    RiCalendarCheckLine,
    RiCalendarEventLine,
    RiCloseLine,
    RiFilterLine,
    RiHashtag,
    RiPriceTag2Line,
    RiProgress3Line,
    RiShareLine,
    RiSpeedLine,
    RiUserLine,
} from "@remixicon/react";

import { RiArrowUpDoubleLine } from "react-icons/ri";
import classNames from "classnames";
import Dropdown from "../../theme/dropdown/Dropdown";
import Styles from "./BoardToolbar.module.scss";
import Users from "../../pickers/users/Users";
import Button from "../../theme/button/Button";
import Priority from "../../pickers/priority/Priority";
import {
    DashboardSettingsFiltersDate, DashboardSettingsFiltersInput, TicketPriority, TicketStatus, UserTicketStatus,
} from "../../../__generated__/api";
import ConversationSources from "../../pickers/conversation-sources/ConversationSources";
import Queues from "../../pickers/queue/Queue";
import RequestTypes from "../../pickers/request-types/RequestTypes";
import Tags from "../../pickers/tags/Tags";
import { Filters as FilterDropdown } from "../../filters/Filters";
import DateRangeCalendarPicker, { DatePreferences } from "../../pickers/date-range/DateRangeCalendarPicker";
import AgentPicker from "../../pickers/agents/Agents";
import TicketStatusPicker from "../../pickers/ticket-status/TicketStatus";

interface FilterTypes {
    assignees?: boolean;
    conversationSources?: boolean;
    forms?: boolean;
    priorities?: boolean;
    statuses?: boolean;
    queues?: boolean;
    requesters?: boolean;
    tags?: boolean;
    userTicketStatus?: boolean;
    createdDate?: boolean;
    lastResponseDate?: boolean;
    requestTypes?: boolean;
}

const DEFAULT_FILTER_TYPES: FilterTypes = {
    assignees: true,
    conversationSources: true,
    forms: true,
    priorities: true,
    statuses: true,
    queues: true,
    requesters: true,
    tags: true,
    userTicketStatus: true,
    createdDate: true,
    lastResponseDate: true,
    requestTypes: true,
};

const USER_STATUS_FILTERS = [
    {
        value: UserTicketStatus.UnassignedOrMine,
        label: "Unassigned or Mine",
    },
    {
        value: UserTicketStatus.Read,
        label: "Read",
    },
    {
        value: UserTicketStatus.Unread,
        label: "Unread",
    },
    {
        value: UserTicketStatus.SlaUpcoming,
        label: "Upcoming SLA",
    },
    {
        value: UserTicketStatus.SlaUpcomingSoon,
        label: "Upcoming SLA Soon",
    },
    {
        value: UserTicketStatus.SlaExpired,
        label: "Overdue SLA",
    },
    {
        value: UserTicketStatus.LastMessageFromAgent,
        label: "Responded by Agent",
    },
    {
        value: UserTicketStatus.LastMessageFromUser,
        label: "No response from Agent",
    },
];

function isDateNotEmpty(date: DashboardSettingsFiltersDate | undefined | null) {
    return date?.endDate != null
        || date?.startDate != null
        || date?.preset != null
        || date?.endDateOffset != null
        || date?.startDateOffset != null;
}

function appliedFilters(filters: DashboardSettingsFiltersInput | undefined, types?: FilterTypes): number {
    const filterTypes = types ?? DEFAULT_FILTER_TYPES;
    return [
        filterTypes.assignees && filters?.assigneeIds?.isNotEmpty,
        filterTypes.conversationSources && filters?.conversationSourceIds?.isNotEmpty,
        filterTypes.forms && filters?.formIds?.isNotEmpty,
        filterTypes.priorities && filters?.priorities?.isNotEmpty,
        filterTypes.statuses && filters?.statuses?.isNotEmpty,
        filterTypes.queues && filters?.queueIds?.isNotEmpty,
        filterTypes.requesters && filters?.requesterIds?.isNotEmpty,
        filterTypes.tags && filters?.tags?.isNotEmpty,
        filterTypes.userTicketStatus && filters?.userTicketStatus?.isNotEmpty,
        filterTypes.createdDate && isDateNotEmpty(filters?.createdDate),
        filterTypes.lastResponseDate && isDateNotEmpty(filters?.lastResponseDate),
    ].count(item => item === true);
}

export function FiltersView({
    onChange,
    value,
    shouldPortal,
    types,
}: {
    onChange: (filter: DashboardSettingsFiltersInput) => void;
    value: DashboardSettingsFiltersInput | undefined;
    shouldPortal?: boolean;
    types?: FilterTypes
}) {
    const filterTypes = types ?? DEFAULT_FILTER_TYPES;
    return (
        <div className="d-flex flex-column gap3-05">
            {filterTypes.assignees && (
                <div className="d-flex">
                    <AgentPicker
                        shouldShowDropdown
                        toggleText="Assignee"
                        className={classNames("flex-grow-1", "dropdown-menu-level-2")}
                        icon={<RiAccountCircleLine />}
                        value={value?.assigneeIds as string[]}
                        isMulti
                        shouldShowSelfFirst
                        searchMaximum={5}
                        onSelected={assigneeIds => {
                            onChange({ ...value, assigneeIds });
                        }}
                    />

                    <Button
                        prefixIcon={<RiCloseLine />}
                        size="small"
                        variant="none"
                        onClick={() => {
                            onChange({ ...value, assigneeIds: [] });
                        }}
                        className={(value?.assigneeIds?.length ?? 0) > 0 ? "visible" : "invisible"}
                    />
                </div>
            )}
            {filterTypes.requesters && (
                <div className="d-flex">
                    <Users
                        shouldShowDropdown
                        icon={<RiUserLine />}
                        toggleText="Requester"
                        className={classNames("flex-grow-1", "dropdown-menu-level-2")}
                        searchMaximum={5}
                        value={value?.requesterIds as string[]}
                        isMulti
                        onSelected={requesterIds => {
                            onChange({ ...value, requesterIds });
                        }}
                    />
                    <Button
                        prefixIcon={<RiCloseLine />}
                        size="small"
                        variant="none"
                        onClick={() => {
                            onChange({ ...value, requesterIds: [] });
                        }}
                        className={(value?.requesterIds?.length ?? 0) > 0 ? "visible" : "invisible"}
                    />
                </div>
            )}
            {filterTypes.priorities && (
                <div className="d-flex">
                    <Priority
                        shouldShowDropdown
                        toggleText="Priority"
                        icon={<RiArrowUpDoubleLine />}
                        className="flex-grow-1"
                        value={value?.priorities as TicketPriority[]}
                        isMulti
                        onSelected={priorities => {
                            onChange({ ...value, priorities });
                        }}
                    />
                    <Button
                        prefixIcon={<RiCloseLine />}
                        size="small"
                        variant="none"
                        onClick={() => {
                            onChange({ ...value, priorities: [] });
                        }}
                        className={(value?.priorities?.length ?? 0) > 0 ? "visible" : "invisible"}
                    />
                </div>
            )}
            {filterTypes.statuses && (
                <div className="d-flex">
                    <TicketStatusPicker
                        shouldShowDropdown
                        toggleText="Statuses"
                        icon={<RiProgress3Line />}
                        className="flex-grow-1"
                        value={value?.statuses as TicketStatus[]}
                        isMulti
                        onSelected={statuses => {
                            onChange({ ...value, statuses });
                        }}
                    />
                    <Button
                        prefixIcon={<RiCloseLine />}
                        size="small"
                        variant="none"
                        onClick={() => {
                            onChange({ ...value, statuses: [] });
                        }}
                        className={(value?.statuses?.length ?? 0) > 0 ? "visible" : "invisible"}
                    />
                </div>
            )}
            {filterTypes.createdDate && (
                <div className="d-flex">
                    <DateRangeCalendarPicker
                        toggleText="Created Date"
                        icon={<RiCalendarCheckLine />}
                        className="flex-grow-1"
                        value={value?.createdDate as DatePreferences}
                        shouldPortal={shouldPortal}
                        onChange={createdDate => {
                            onChange({ ...value, createdDate });
                        }}
                    />
                    <Button
                        prefixIcon={<RiCloseLine />}
                        size="small"
                        variant="none"
                        onClick={() => {
                            onChange({ ...value, createdDate: undefined });
                        }}
                        className={isDateNotEmpty(value?.createdDate)
                            ? "visible"
                            : "invisible"}
                    />
                </div>
            )}
            {filterTypes.lastResponseDate && (
                <div className="d-flex">
                    <DateRangeCalendarPicker
                        toggleText="Last Response"
                        icon={<RiCalendarEventLine />}
                        className="flex-grow-1"
                        value={value?.lastResponseDate as DatePreferences}
                        shouldPortal={shouldPortal}
                        onChange={lastResponseDate => {
                            onChange({ ...value, lastResponseDate });
                        }}
                    />
                    <Button
                        prefixIcon={<RiCloseLine />}
                        size="small"
                        variant="none"
                        onClick={() => {
                            onChange({ ...value, lastResponseDate: undefined });
                        }}
                        className={isDateNotEmpty(value?.lastResponseDate)
                            ? "visible"
                            : "invisible"}
                    />
                </div>
            )}
            {filterTypes.conversationSources && (
                <div className="d-flex">
                    <ConversationSources
                        shouldShowDropdown
                        toggleText="Source"
                        shouldIncludeEmail
                        shouldIncludeTags={false}
                        icon={<RiShareLine />}
                        className="flex-grow-1"
                        value={value?.conversationSourceIds as string[]}
                        isMulti
                        searchMinimum={5}
                        onSelected={conversationSourceIds => {
                            onChange({ ...value, conversationSourceIds });
                        }}
                    />
                    <Button
                        prefixIcon={<RiCloseLine />}
                        size="small"
                        variant="none"
                        onClick={() => {
                            onChange({ ...value, conversationSourceIds: [] });
                        }}
                        className={(value?.conversationSourceIds?.length ?? 0) > 0 ? "visible" : "invisible"}
                    />
                </div>
            )}
            {filterTypes.requestTypes && (
                <div className={`d-flex ${Styles["hide-empty-filter"]}`}>
                    <RequestTypes
                        shouldShowDropdown
                        toggleText="Request Type"
                        icon={<RiPriceTag2Line />}
                        className="flex-grow-1"
                        value={value?.formIds as string[]}
                        isMulti
                        onSelected={formIds => {
                            onChange({ ...value, formIds });
                        }}
                    />
                    <Button
                        prefixIcon={<RiCloseLine />}
                        size="small"
                        variant="none"
                        onClick={() => {
                            onChange({ ...value, formIds: [] });
                        }}
                        className={(value?.formIds?.length ?? 0) > 0 ? "visible" : "invisible"}
                    />
                </div>
            )}
            {filterTypes.queues && (
                <div className={`d-flex ${Styles["hide-empty-filter"]}`}>
                    <Queues
                        shouldShowDropdown
                        toggleText="Queue"
                        icon={<RiSpeedLine />}
                        className="flex-grow-1"
                        value={value?.queueIds as string[]}
                        isMulti
                        onSelected={queueIds => {
                            onChange({ ...value, queueIds });
                        }}
                    />
                    <Button
                        prefixIcon={<RiCloseLine />}
                        size="small"
                        variant="none"
                        onClick={() => {
                            onChange({ ...value, queueIds: [] });
                        }}
                        className={(value?.queueIds?.length ?? 0) > 0 ? "visible" : "invisible"}
                    />

                </div>
            )}
            {filterTypes.tags && (
                <div className="d-flex">
                    <Tags
                        shouldShowDropdown
                        toggleText="Tags"
                        icon={<RiHashtag />}
                        className="flex-grow-1"
                        value={value?.tags as string[]}
                        isMulti
                        onSelected={tags => {
                            onChange({ ...value, tags });
                        }}
                    />
                    <Button
                        prefixIcon={<RiCloseLine />}
                        size="small"
                        variant="none"
                        onClick={() => {
                            onChange({ ...value, tags: [] });
                        }}
                        className={(value?.tags?.length ?? 0) > 0 ? "visible" : "invisible"}
                    />
                </div>
            )}
            {filterTypes.userTicketStatus && (
                <div className="d-flex">
                    <FilterDropdown
                        icon={<RiBookMarkedLine />}
                        isMulti
                        toggleText="Read"
                        categories={USER_STATUS_FILTERS}
                        className="flex-grow-1"
                        selectedCategories={new Set(value?.userTicketStatus ?? [])}
                        onSelected={selected => {
                            onChange({ ...value, userTicketStatus: Array.from(selected) as UserTicketStatus[] });
                        }}
                    />
                    <Button
                        prefixIcon={<RiCloseLine />}
                        size="small"
                        variant="none"
                        onClick={() => {
                            onChange({ ...value, userTicketStatus: [] });
                        }}
                        className={(value?.userTicketStatus?.length ?? 0) > 0 ? "visible" : "invisible"}
                    />
                </div>
            )}

        </div>

    );
}
FiltersView.defaultProps = {
    shouldPortal: undefined,
    types: DEFAULT_FILTER_TYPES,
};

export default function Filters({
    onChange,
    value,
    types,
}: {
    onChange: (filter: DashboardSettingsFiltersInput) => void;
    value: DashboardSettingsFiltersInput | undefined | null;
    types?: FilterTypes
}) {
    const [filters, setFilters] = useState<DashboardSettingsFiltersInput | undefined>(value ?? {});
    useEffect(() => {
        setFilters(value as DashboardSettingsFiltersInput | undefined);
    }, [value]);
    const [shouldShow, setShouldShow] = useState(false);
    const countFilters = appliedFilters(filters, types);
    return (
        <Dropdown
            autoClose="outside"
            show={shouldShow}
            onToggle={() => {
                setShouldShow(!shouldShow);
                if (shouldShow) {
                    setFilters(value as DashboardSettingsFiltersInput | undefined);
                }
            }}
        >
            <Dropdown.Toggle
                variant="filter"
                prefixIcon={<RiFilterLine />}
                onClick={() => setShouldShow(!shouldShow)}
                isActive={countFilters > 0}
            >
                Filters
                {countFilters > 0 && `: ${countFilters} selected`}
            </Dropdown.Toggle>
            <Dropdown.Menu className={`p3-2 ${Styles["panel-filters"]}`}>
                <FiltersView
                    onChange={setFilters}
                    value={filters}
                    types={types}
                />

                <div className="d-flex w-100 justify-content-between m3t-2">
                    <Button
                        onClick={() => {
                            const empty = {};
                            setFilters(empty);
                            onChange(empty);
                            setShouldShow(false);
                        }}
                        variant="ghost-blue"
                        size="small"
                    >
                        Clear All Filters
                    </Button>
                    <Button
                        onClick={() => {
                            onChange(filters ?? {});
                            setShouldShow(false);
                        }}
                    >
                        Filter
                    </Button>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
}

Filters.defaultProps = {
    types: DEFAULT_FILTER_TYPES,
};
